import {FormBuilder, FormGroup, Validators} from "@angular/forms";
import {distinctUntilChanged, map} from "rxjs/operators";
import {Product, Scope, StandardName} from "../../../../store/state/order.state";
import {OrderActions} from "../../../../store/actions/order.actions";
import {Store} from "@ngrx/store";
import {AppState} from "../../../../store/state/app.state";
import {FormService} from "../../../../umbrella-common/services/form.service";
import {BaseFormControlHandler} from "./form-control.handler";
import {Injectable} from "@angular/core";
import {UmbrellaValidators} from "../../../../umbrella-common/validation/umbrella-validators";

@Injectable()
export class CompanyScopesHandler extends BaseFormControlHandler {

	private previousScopes: Scope[] = [];
	private allScopes: Scope[] = [];

	private scopeToStringVersion(scope: Scope): string {
		return scope.code + ' - ' + scope.name;
	}

	constructor(private fb: FormBuilder, private store: Store<AppState>) {
		super();
	}

	buildControls(form: FormGroup, product: Product, params?: any) {
		this.allScopes = params.allScopes;
		this.previousScopes = product.scopes;
		form.addControl(`companyScopes${(product.standard.name)}`, this.fb.control(product.scopes.map(sc => sc.code + ' - ' + sc.name), [Validators.required, Validators.min(1)]));
		this.addSubscription(product.standard.name, form.controls['companyScopes' + product.standard.name].valueChanges
			.pipe(distinctUntilChanged(), map(scopesString => this.toScopeArray(form, scopesString, product.standard.name)))
			.subscribe(scopes => this.handleScopesChange(product, form, scopes)));

		if (this.scopeQuestionsPresent(product)) {
			this.getScopesWithQuestions(product).forEach(scope => this.addScopeQuestionsControlsIfNeeded(form, scope, product));
		}

		this.selectScopeForSingleScopeProduct(form, product);
	}

	override handleSpecificCopying(formService: FormService, controlName: string, srcForm: string, dstForm: string) {
		if (controlName == 'companyScopes') {
			const srcControl = formService.getFormControl(srcForm, 'companyScopes' + srcForm);
			const dstControl = formService.getFormControl(dstForm, 'companyScopes' + dstForm);

			if (formService.hasValue(srcControl) && formService.hasNoValue(dstControl)) {
				const valuesToCopy = this.removeNotAllowedScopes(srcControl.value, srcForm as StandardName, dstForm as StandardName);
				dstControl.setValue(valuesToCopy);
			}
		}
	}

	protected fillProduct(formService: FormService, srcForm: string, product: Product): void {
		product.scopes = this.getCompanyScopes(formService, srcForm);
	}

	private getCompanyScopes(formService: FormService, formKey: string): Scope[] {
		return (formService.getValueFor('companyScopes', formKey) as string[])
			.map(scopeString => {
				const code = scopeString.split('-')[0].trim();
				return this.allScopes.find(s => s.code === code && s.standardName === formKey);
			}).map(scope => {
				if (this.hasQuestions(scope)) {
					scope.questions.forEach((question, index) => {
						question.answer = this.getQuestionAnswer(formService, formKey, scope, index);
					});
				}

				return scope;
			});
	}

	private getQuestionAnswer(formService: FormService, formKey: string, scope: Scope, questionIndex: number) {
		const controlName = this.getScopeQuestionControlNamePrefix(scope, questionIndex);
		return formService.getValueFor(controlName, formKey);
	}

	private removeNotAllowedScopes(scopes: any, copyFromStandardName: StandardName, copyToStandardName: StandardName): any {
		const finalScopesList = [];
		const allowedScopesForCopy = this.getAllowedScopeForCopy(copyFromStandardName, copyToStandardName);

		for (const scope of scopes) {
			if (!allowedScopesForCopy.some(sc => this.scopeToStringVersion(sc) === scope)) {
				continue;
			}
			finalScopesList.push(scope);
		}

		return finalScopesList;
	}

	private getAllowedScopeForCopy(copyFromStandardName: StandardName, copyToStandardName: StandardName) {
		const allPossibleSrcScopes = this.allScopes.filter(scope => scope.standardName === copyFromStandardName);
		const allPossibleDestScopes = this.allScopes.filter(scope => scope.standardName === copyToStandardName);
		return allPossibleSrcScopes.filter(scope => allPossibleDestScopes.some(sc => scope.name === sc.name));
	}

	private handleScopesChange(product: Product, form: FormGroup, scopes: Scope[]) {
		if (product.standard.name === StandardName.ISO_14001 || product.standard.name === StandardName.ISO_45001) {
			this.removeControlsIfNeeded(form, product.standard.name, scopes);
			this.previousScopes = scopes;
		}

		this.store.dispatch(OrderActions.SET_CLIENT_SCOPES({standardName: product.standard.name, scopes}));

		if (product.standard.name === StandardName.ISO_14001 || product.standard.name === StandardName.ISO_45001) {
			scopes.filter(selectedScope => this.hasQuestions(selectedScope)).forEach(selectedScopeWithQuestion => {
				this.addScopeQuestionsControlsIfNeeded(form, selectedScopeWithQuestion, product);
			});
		}
	}

	private selectScopeForSingleScopeProduct(form: FormGroup, product: Product) {
		const scopesForProduct = this.allScopes.filter(sc => sc.standardName == product.standard.name);
		if (scopesForProduct.length == 1) {
			form.controls[`companyScopes${(product.standard.name)}`].setValue(scopesForProduct.map(sc => sc.code + ' - ' + sc.name));
		}
	}

	private addScopeQuestionsControlsIfNeeded(form: FormGroup, selectedScopeWithQuestion: Scope, product: Product) {
		selectedScopeWithQuestion.questions.forEach((question, index) => {
			const control = this.fb.control(question.answer, UmbrellaValidators.radiobuttonRequired());
			this.addControlIfNotPresent(form, product.standard.name, this.getScopeQuestionControlNamePrefix(selectedScopeWithQuestion, index), control);
			this.addSubscription(product.standard.name, control.valueChanges.pipe(distinctUntilChanged())
				.subscribe(answer => {
					this.store.dispatch(OrderActions.SET_SCOPE_ANSWER({
						standardName: product.standard.name,
						scopeCode: selectedScopeWithQuestion.code,
						questionType: question.type,
						answer
					}));
				}));
		});
	}

	private removeControlsIfNeeded(form: FormGroup, standardName: StandardName, currentScopes: Scope[]) {
		this.previousScopes.filter(previousScope => !currentScopes.some(cs => cs.code === previousScope.code)).filter(previousScope => previousScope.questions != null)
			.forEach(previousScope => {
				previousScope.questions.forEach((question, index) => {
					this.removeControlIfPresent(form, standardName, this.getScopeQuestionControlNamePrefix(previousScope, index));
				});
			});
	}

	private toScopeArray(form: FormGroup, scopeStrings: string[], standardName: StandardName): Scope[] {
		return scopeStrings == null ? [] : scopeStrings.map(ss => this.toScope(form, ss, standardName));
	}

	private toScope(form: FormGroup, scopeString: string, standardName: StandardName): Scope {
		const scopeCode: string = scopeString.split(' - ')[0];
		const scope = this.allScopes.find(sc => sc.code === scopeCode && sc.standardName === standardName);

		// following is because after mapping from scopestring to scope answers are set to null
		if (this.hasQuestions(scope)) {
			scope.questions.forEach((question, index) => {
				question.answer = this.getQuestionAnswerOrNull(form, standardName, scope, index);
			});
		}

		return scope;
	}

	private getScopeQuestionControlNamePrefix(scope: Scope, questionIndex: number): string {
		return 'scope-question-'.concat(scope.code).concat('-').concat(scope.questions[questionIndex].type.toLowerCase());
	}

	private scopeQuestionsPresent(product: Product): boolean {
		return product.scopes.some(scope => this.hasQuestions(scope));
	}

	private getScopesWithQuestions(product: Product): Scope[] {
		return product.scopes.filter(scope => this.hasQuestions(scope));
	}

	private hasQuestions(scope: Scope) {
		return scope.questions != null && scope.questions.length > 0;
	}

	private getQuestionAnswerOrNull(form: FormGroup, formKey: string, scope: Scope, questionIndex: number) {
		const controlName = this.getScopeQuestionControlNamePrefix(scope, questionIndex);
		const control = form.controls[controlName + formKey];
		return !!control ? control.value : null;
	}
}
